import { useState, useEffect } from "react";
import { getUsers, createUser, updateUser, deleteUser, getRoleHierarchy } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Users, Plus, Pencil, Trash2, Loader2, Shield, UserCircle, Mail, Crown, ShieldCheck, UserCog, Eye } from "lucide-react";
import { format, parseISO } from "date-fns";
import { toast } from "sonner";
import { usePermissions } from "@/context/PermissionContext";

// Role display configuration
const ROLE_CONFIG = {
  master_admin: { 
    label: "Master Admin", 
    icon: Crown, 
    color: "bg-yellow-500", 
    badgeClass: "bg-yellow-100 text-yellow-800 border-yellow-300"
  },
  admin: { 
    label: "Admin", 
    icon: ShieldCheck, 
    color: "bg-blue-500", 
    badgeClass: "bg-blue-100 text-blue-800 border-blue-300"
  },
  operational_agent: { 
    label: "Operational Agent", 
    icon: UserCog, 
    color: "bg-green-500", 
    badgeClass: "bg-green-100 text-green-800 border-green-300"
  },
  exposition: { 
    label: "Exposition (Demo)", 
    icon: Eye, 
    color: "bg-amber-500", 
    badgeClass: "bg-amber-100 text-amber-800 border-amber-300"
  },
  staff: { 
    label: "Operational Agent", 
    icon: UserCog, 
    color: "bg-green-500", 
    badgeClass: "bg-green-100 text-green-800 border-green-300"
  }
};

const initialFormData = {
  email: "",
  password: "",
  full_name: "",
  role: "operational_agent"
};

export default function UserManagementPage() {
  const { hasPermission } = usePermissions();
  const [users, setUsers] = useState([]);
  const [roleHierarchy, setRoleHierarchy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [submitting, setSubmitting] = useState(false);

  // Permission checks
  const canCreate = hasPermission("users", "create");
  const canEdit = hasPermission("users", "edit");
  const canDelete = hasPermission("users", "delete");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [usersRes, hierarchyRes] = await Promise.all([
        getUsers(),
        getRoleHierarchy()
      ]);
      setUsers(usersRes.data);
      setRoleHierarchy(hierarchyRes.data);
    } catch (error) {
      toast.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await getUsers();
      setUsers(response.data);
    } catch (error) {
      toast.error("Failed to fetch users");
    }
  };

  const handleOpenDialog = (user = null) => {
    if (user) {
      setSelectedUser(user);
      setFormData({
        email: user.email,
        password: "",
        full_name: user.full_name,
        role: user.role
      });
    } else {
      setSelectedUser(null);
      setFormData(initialFormData);
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedUser(null);
    setFormData(initialFormData);
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!formData.email || !formData.full_name) {
      toast.error("Please fill in required fields");
      return;
    }

    if (!selectedUser && !formData.password) {
      toast.error("Password is required for new users");
      return;
    }

    setSubmitting(true);
    try {
      if (selectedUser) {
        const updateData = {
          full_name: formData.full_name,
          role: formData.role
        };
        // Include password if it was changed
        if (formData.password && formData.password.trim()) {
          updateData.password = formData.password;
        }
        await updateUser(selectedUser.id, updateData);
        toast.success("User updated successfully");
      } else {
        await createUser(formData);
        toast.success("User created successfully");
      }
      handleCloseDialog();
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save user");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedUser) return;

    try {
      await deleteUser(selectedUser.id);
      toast.success("User deleted successfully");
      setDeleteDialogOpen(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      toast.error("Failed to delete user");
    }
  };

  const handleToggleActive = async (user) => {
    try {
      await updateUser(user.id, { is_active: !user.is_active });
      toast.success(`User ${user.is_active ? "deactivated" : "activated"}`);
      fetchUsers();
    } catch (error) {
      toast.error("Failed to update user status");
    }
  };

  const openDeleteDialog = (user) => {
    setSelectedUser(user);
    setDeleteDialogOpen(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="w-10 h-10 animate-spin text-[#d4af37]" />
      </div>
    );
  }

  return (
    <div className="animate-fade-in" data-testid="user-management-page">
      {/* Sticky Header */}
      <div className="sticky-page-header flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-[#0f172a] dark:text-white font-['Outfit']">User Management</h1>
          <p className="text-[#64748b] dark:text-gray-400 mt-1">Manage user accounts and permissions</p>
        </div>
        {canCreate && (
          <Button
            onClick={() => handleOpenDialog()}
            className="bg-[#d4af37] hover:bg-[#b5952f] text-[#0f172a] font-semibold"
            data-testid="add-user-btn"
          >
            <Plus size={20} className="mr-2" />
            Add User
          </Button>
        )}
      </div>

      {/* Role Hierarchy Info */}
      {roleHierarchy && (
        <div className="mb-6 p-3 bg-gradient-to-r from-[#0f172a] to-[#1e293b] rounded-lg text-white text-sm">
          <div className="flex items-center gap-4 flex-wrap">
            <span className="text-gray-400">You are:</span>
            <span className={`font-medium ${roleHierarchy.is_master_admin ? 'text-yellow-400' : roleHierarchy.current_role === 'admin' ? 'text-blue-400' : 'text-green-400'}`}>
              {roleHierarchy.is_master_admin ? 'Master Admin' : ROLE_CONFIG[roleHierarchy.current_role]?.label || roleHierarchy.current_role}
            </span>
            {roleHierarchy.creatable_roles?.length > 0 && (
              <>
                <span className="text-gray-400">|</span>
                <span className="text-gray-400">Can create:</span>
                <span className="text-white">{roleHierarchy.creatable_roles.map(r => ROLE_CONFIG[r]?.label || r).join(', ')}</span>
              </>
            )}
          </div>
        </div>
      )}

      {/* Users List */}
      {users.length === 0 ? (
        <div className="empty-state">
          <Users className="mx-auto" />
          <h3>No Users Yet</h3>
          <p>Start by adding users to the system.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {users.map((user) => {
            const roleConfig = ROLE_CONFIG[user.role] || ROLE_CONFIG.operational_agent;
            const RoleIcon = roleConfig.icon;
            const isMasterAdmin = user.role === 'master_admin';
            const isAdmin = user.role === 'admin';
            const canEditThisUser = canEdit && (roleHierarchy?.is_master_admin || (!isMasterAdmin && !isAdmin));
            const canDeleteThisUser = canDelete && (roleHierarchy?.is_master_admin ? !isMasterAdmin : (!isMasterAdmin && !isAdmin));
            const canToggleThisUser = roleHierarchy?.is_master_admin || (!isMasterAdmin && !isAdmin);
            
            return (
              <Card
                key={user.id}
                className={`border-[#e2e8f0] card-hover ${!user.is_active ? "opacity-60" : ""}`}
                data-testid={`user-card-${user.id}`}
              >
                <CardContent className="p-5">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center ${roleConfig.color}`}>
                        <RoleIcon size={24} className="text-white" />
                      </div>
                      <div>
                        <p className="font-semibold text-[#0f172a] flex items-center gap-1">
                          {user.full_name}
                          {isMasterAdmin && <Crown size={14} className="text-yellow-500" />}
                        </p>
                        <Badge className={`${roleConfig.badgeClass} border`}>
                          <RoleIcon size={12} className="mr-1" />
                          {roleConfig.label}
                        </Badge>
                        {user.totp_enabled && (
                          <Badge className="ml-1 border bg-emerald-50 text-emerald-700 border-emerald-300" data-testid={`user-2fa-badge-${user.id}`}>
                            <ShieldCheck size={12} className="mr-1" />
                            2FA
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2 text-sm mb-4">
                    <div className="flex items-center gap-2 text-[#64748b]">
                      <Mail size={14} />
                      <span className="truncate">{user.email}</span>
                    </div>
                    <div className="text-[#64748b]">
                      Created: {format(parseISO(user.created_at), "dd MMM yyyy")}
                    </div>
                  </div>

                  <div className="flex items-center justify-between pt-4 border-t border-[#e2e8f0]">
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={user.is_active}
                        onCheckedChange={() => handleToggleActive(user)}
                        disabled={!canToggleThisUser}
                        data-testid={`toggle-user-${user.id}`}
                      />
                      <span className="text-sm text-[#64748b]">
                        {user.is_active ? "Active" : "Inactive"}
                      </span>
                    </div>
                    <div className="flex gap-1">
                      {canEditThisUser && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleOpenDialog(user)}
                          className="text-[#64748b] hover:text-[#0f172a]"
                          data-testid={`edit-user-${user.id}`}
                        >
                          <Pencil size={16} />
                        </Button>
                      )}
                      {canDeleteThisUser && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openDeleteDialog(user)}
                          className="text-[#64748b] hover:text-red-600"
                          data-testid={`delete-user-${user.id}`}
                        >
                          <Trash2 size={16} />
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Add/Edit User Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md bg-white border-[#e2e8f0]">
          <DialogHeader>
            <DialogTitle className="text-xl font-['Outfit'] text-[#0f172a]">
              {selectedUser ? "Edit User" : "Add New User"}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="full_name" className="text-[#0f172a]">Full Name *</Label>
              <Input
                id="full_name"
                value={formData.full_name}
                onChange={(e) => handleChange("full_name", e.target.value)}
                placeholder="John Doe"
                className="bg-white border-[#e2e8f0] text-[#0f172a]"
                data-testid="user-fullname-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-[#0f172a]">Email *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => handleChange("email", e.target.value)}
                placeholder="user@example.com"
                disabled={!!selectedUser}
                className="bg-white border-[#e2e8f0] text-[#0f172a]"
                data-testid="user-email-input"
              />
            </div>

            {!selectedUser && (
              <div className="space-y-2">
                <Label htmlFor="password" className="text-[#0f172a]">Password *</Label>
                <Input
                  id="password"
                  type="password"
                  value={formData.password}
                  onChange={(e) => handleChange("password", e.target.value)}
                  placeholder="Enter password"
                  className="bg-white border-[#e2e8f0] text-[#0f172a]"
                  data-testid="user-password-input"
                />
              </div>
            )}

            {selectedUser && (
              <div className="space-y-2">
                <Label htmlFor="password" className="text-[#0f172a]">New Password (leave empty to keep current)</Label>
                <Input
                  id="password"
                  type="password"
                  value={formData.password}
                  onChange={(e) => handleChange("password", e.target.value)}
                  placeholder="Enter new password or leave empty"
                  className="bg-white border-[#e2e8f0] text-[#0f172a]"
                  data-testid="user-password-input"
                />
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="role">Role</Label>
              <Select
                value={formData.role}
                onValueChange={(value) => handleChange("role", value)}
              >
                <SelectTrigger className="bg-white border-[#e2e8f0] text-[#0f172a]" data-testid="user-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="operational_agent">
                    <div className="flex items-center gap-2">
                      <UserCog size={14} className="text-green-600" />
                      Operational Agent
                    </div>
                  </SelectItem>
                  {roleHierarchy?.is_master_admin && (
                    <SelectItem value="admin">
                      <div className="flex items-center gap-2">
                        <ShieldCheck size={14} className="text-blue-600" />
                        Admin
                      </div>
                    </SelectItem>
                  )}
                  {roleHierarchy?.is_master_admin && (
                    <SelectItem value="exposition">
                      <div className="flex items-center gap-2">
                        <Eye size={14} className="text-amber-600" />
                        Exposition (Demo — visual only)
                      </div>
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
              {!roleHierarchy?.is_master_admin && (
                <p className="text-xs text-gray-500">Only Master Admin can create Admin users</p>
              )}
            </div>
          </div>

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={handleCloseDialog} className="border-[#e2e8f0] text-[#64748b]">
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={submitting}
              className="bg-[#d4af37] hover:bg-[#b5952f] text-[#0f172a]"
              data-testid="save-user-btn"
            >
              {submitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                selectedUser ? "Update User" : "Create User"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#0f172a]">Delete User</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete {selectedUser?.full_name}? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-[#e2e8f0]">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-user-btn"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
